import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Clock,
  Desktop,
  DeviceMobile,
  DownloadSimple,
  DeviceTablet,
  Eye,
  Gauge,
  Heart,
  Hourglass,
  Star,
  Television,
  TrendUp,
  Users,
  Warning,
  type Icon,
} from '@/core/ui/icons';
import { useState, type ReactNode } from 'react';
import {
  AreaChart,
  BarList,
  Card,
  cn,
  ColumnChart,
  EmptyState,
  ErrorState,
  Select,
  Skeleton,
} from '@/core/ui';
import { useLessonInsights } from '../application/insights';
import { DEVICE_LABELS } from '../domain/engagement';
import {
  clock,
  dayLabel,
  deltaPercent,
  longDayLabel,
  peakHour,
  PERIODS,
  watchTime,
  WEEKDAYS,
  type LessonInsights,
  type PeriodDays,
} from '../domain/insights';
import { AudienceView } from './AudienceView';

export type InsightsTab = 'geral' | 'retencao' | 'publico' | 'pessoas';

const TABS: { id: InsightsTab; label: string }[] = [
  { id: 'geral', label: 'Visão geral' },
  { id: 'retencao', label: 'Retenção' },
  { id: 'publico', label: 'Público' },
  { id: 'pessoas', label: 'Pessoas' },
];

interface Props {
  lessonId: string;
  days: PeriodDays;
  onDaysChange: (days: PeriodDays) => void;
  tab: InsightsTab;
  onTabChange: (tab: InsightsTab) => void;
  onOpenPerson?: (userId: string) => void;
}

/**
 * Insights de uma aula, no espírito do YouTube Studio: alcance no tempo,
 * retenção segundo a segundo, quando e de onde assistem, e quem assistiu.
 */
export function LessonInsightsView({ lessonId, days, onDaysChange, tab, onTabChange, onOpenPerson }: Props) {
  // Na impressão quem aparece é o <LessonReport>, com tudo numa página só:
  // um PDF não tem aba para clicar.
  const insights = useLessonInsights(lessonId, days);

  return (
    <div>
      {/* Filtros numa linha só, acima de tudo o que eles afetam. */}
      <div className="mb-6 flex flex-col gap-3 border-b border-line sm:flex-row sm:items-end sm:justify-between">
        <div role="tablist" aria-label="Insights" className="-mb-px flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => onTabChange(t.id)}
              className={cn(
                'border-b-2 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors',
                tab === t.id ? 'border-brand-400 text-fg' : 'border-transparent text-fg-subtle hover:text-fg',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab !== 'pessoas' && (
          <Select
            aria-label="Período"
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value) as PeriodDays)}
            className="mb-2 h-9 w-full sm:w-48"
          >
            {PERIODS.map((p) => (
              <option key={p.days} value={p.days}>
                {p.label}
              </option>
            ))}
          </Select>
        )}
      </div>

      {tab === 'pessoas' ? (
        <AudienceView lessonId={lessonId} onOpenPerson={onOpenPerson} />
      ) : insights.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-72 rounded-2xl" />
        </div>
      ) : insights.isError ? (
        <ErrorState error={insights.error} onRetry={() => void insights.refetch()} />
      ) : (
        // Recarregando: mantém o quadro anterior, só mais apagado.
        <div className={cn('transition-opacity', insights.isPlaceholderData && 'opacity-60')}>
          {tab === 'geral' && <Overview data={insights.data} onTabChange={onTabChange} />}
          {tab === 'retencao' && <Retention data={insights.data} />}
          {tab === 'publico' && <Audience data={insights.data} />}
        </div>
      )}
    </div>
  );
}

// ───────────── visão geral ─────────────

type Metric = 'views' | 'viewers' | 'watched_s';

function Overview({ data, onTabChange }: { data: LessonInsights; onTabChange: (t: InsightsTab) => void }) {
  const [metric, setMetric] = useState<Metric>('views');
  const t = data.totals;
  const prev = data.previous;
  const period = PERIODS.find((p) => p.days === data.days)?.label.toLowerCase() ?? '';
  const compare = data.days ? `vs. ${data.days} dias anteriores` : undefined;

  const metricFormat: Record<Metric, (v: number) => string> = {
    views: (v) => v.toLocaleString('pt-BR'),
    viewers: (v) => v.toLocaleString('pt-BR'),
    watched_s: watchTime,
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        {/* As métricas são as abas do gráfico, como no Studio. */}
        <div
          role="tablist"
          aria-label="Métrica do gráfico"
          className="grid grid-cols-2 border-b border-line lg:grid-cols-4"
        >
          <MetricTab
            active={metric === 'views'}
            onClick={() => setMetric('views')}
            label="Visualizações"
            value={t.views.toLocaleString('pt-BR')}
            delta={deltaPercent(t.views, prev?.views)}
            compare={compare}
          />
          <MetricTab
            active={metric === 'viewers'}
            onClick={() => setMetric('viewers')}
            label="Espectadores únicos"
            value={t.viewers.toLocaleString('pt-BR')}
            delta={deltaPercent(t.viewers, prev?.viewers)}
            compare={compare}
          />
          <MetricTab
            active={metric === 'watched_s'}
            onClick={() => setMetric('watched_s')}
            label="Tempo de exibição"
            value={watchTime(t.watched_s)}
            delta={deltaPercent(t.watched_s, prev?.watched_s)}
            compare={compare}
          />
          <div className="border-line p-4 lg:border-l">
            <p className="text-xs text-fg-subtle">Duração média da visualização</p>
            <p className="mt-1 text-2xl font-semibold">{clock(t.avg_view_duration_s)}</p>
            <p className="mt-1 text-xs text-fg-subtle">
              {t.avg_percent_viewed.toLocaleString('pt-BR')}% da aula de {clock(data.duration_s)}
            </p>
          </div>
        </div>
        <div className="p-4">
          {t.views === 0 ? (
            <p className="py-16 text-center text-sm text-fg-subtle">Nenhuma visualização {period}.</p>
          ) : (
            <AreaChart
              ariaLabel={`${TAB_METRIC_LABEL[metric]} por dia`}
              points={data.daily.map((d) => ({
                label: dayLabel(d.date),
                value: d[metric],
                detail: longDayLabel(d.date),
              }))}
              formatY={metricFormat[metric]}
              formatTooltip={(p) => ({
                value:
                  `${metricFormat[metric](p.value)} ${metric === 'watched_s' ? '' : TAB_METRIC_LABEL[metric].toLowerCase()}`.trim(),
                label: p.detail ?? p.label,
              })}
              xLabels={data.daily.length > 60 ? 6 : 7}
              height={260}
            />
          )}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <SectionTitle
            title="Retenção do público"
            action={
              <button
                type="button"
                onClick={() => onTabChange('retencao')}
                className="text-xs font-medium text-brand-400 hover:underline"
              >
                Ver detalhes
              </button>
            }
          />
          {data.retention.length === 0 ? (
            <p className="py-10 text-center text-sm text-fg-subtle">Sem reprodução no período.</p>
          ) : (
            <>
              <p className="mb-3 text-sm text-fg-muted">
                {data.intro_retention !== null && (
                  <>
                    <strong className="text-fg">{data.intro_retention.toLocaleString('pt-BR')}%</strong> ainda
                    assistiam depois de {clock(Math.min(30, data.duration_s))}.{' '}
                  </>
                )}
                Duração média: <strong className="text-fg">{clock(t.avg_view_duration_s)}</strong>.
              </p>
              <RetentionChart data={data} height={160} />
            </>
          )}
        </Card>

        <Card className="p-5">
          <SectionTitle title="Engajamento" />
          <dl className="grid grid-cols-2 gap-4">
            <Figure
              icon={Heart}
              label="Curtidas"
              value={t.likes.toLocaleString('pt-BR')}
              hint={t.viewers ? `${Math.round((t.likes / t.viewers) * 100)}% de quem assistiu` : undefined}
            />
            <Figure
              icon={CheckCircle}
              label="Concluíram"
              value={`${Math.round(t.completion_rate * 100)}%`}
              hint="dos espectadores"
            />
            <Figure
              icon={Star}
              label="Avaliação"
              value={
                data.ratings.count
                  ? data.ratings.average.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                  : '—'
              }
              hint={
                data.ratings.count
                  ? `${data.ratings.count.toLocaleString('pt-BR')} ${data.ratings.count === 1 ? 'nota' : 'notas'}`
                  : 'ainda sem notas'
              }
            />
            <Figure
              icon={DownloadSimple}
              label="Downloads"
              value={data.downloads.toLocaleString('pt-BR')}
              hint="materiais da aula"
            />
            <Figure icon={Gauge} label="Velocidade média" value={`${t.avg_speed.toLocaleString('pt-BR')}×`} />
            <Figure
              icon={Hourglass}
              label="Tempo carregando"
              value={`${(t.rebuffer_rate * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
              hint="do tempo de reprodução"
              warn={t.rebuffer_rate > 0.02}
            />
          </dl>
        </Card>
      </div>
    </div>
  );
}

const TAB_METRIC_LABEL: Record<Metric, string> = {
  views: 'Visualizações',
  viewers: 'Espectadores',
  watched_s: 'Tempo de exibição',
};

function MetricTab({
  active,
  onClick,
  label,
  value,
  delta,
  compare,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  value: string;
  delta: number | null;
  compare?: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'relative border-line p-4 text-left transition-colors not-first:border-l max-lg:nth-3:border-l-0 max-lg:nth-[n+3]:border-t',
        active ? 'bg-surface-2/60' : 'hover:bg-surface-2/30',
      )}
    >
      {active && <span aria-hidden className="absolute inset-x-0 top-0 h-0.5 bg-brand-400" />}
      <span className="block text-xs text-fg-subtle">{label}</span>
      <span className="mt-1 block text-2xl font-semibold">{value}</span>
      <span className="mt-1 flex items-center gap-1 text-xs text-fg-subtle">
        {delta === null ? (
          compare ? (
            'sem base para comparar'
          ) : (
            ' '
          )
        ) : (
          <>
            {delta > 0 ? (
              <ArrowUp aria-hidden className="size-3 text-teal-700" />
            ) : delta < 0 ? (
              <ArrowDown aria-hidden className="size-3 text-fg" />
            ) : null}
            <span className="font-medium text-fg-muted">
              {delta > 0 ? '+' : ''}
              {delta}%
            </span>
            {compare}
          </>
        )}
      </span>
    </button>
  );
}

// ───────────── retenção ─────────────

function RetentionChart({
  data,
  height = 280,
  drops = false,
}: {
  data: LessonInsights;
  height?: number;
  drops?: boolean;
}) {
  const pts = data.retention;
  const indexOf = (s: number) =>
    pts.reduce(
      (best, p, i) => (Math.abs(p.position_s - s) < Math.abs(pts[best]!.position_s - s) ? i : best),
      0,
    );
  return (
    <AreaChart
      ariaLabel="Retenção do público ao longo da aula"
      points={pts.map((p) => ({ label: clock(p.position_s), value: p.percent }))}
      yMax={100}
      formatY={(v) => `${v}%`}
      formatTooltip={(p) => ({
        value: `${p.value.toLocaleString('pt-BR')}% assistindo`,
        label: `em ${p.label}`,
      })}
      highlights={drops ? data.drops.map((d) => ({ from: indexOf(d.from_s), to: indexOf(d.to_s) })) : []}
      height={height}
    />
  );
}

function Retention({ data }: { data: LessonInsights }) {
  const t = data.totals;
  if (data.retention.length === 0) {
    return (
      <EmptyState
        title="Ainda sem reprodução neste período"
        description="A curva aparece quando alguém assistir à aula. Ela mostra, momento a momento, quantos continuam assistindo."
      />
    );
  }
  return (
    <div className="space-y-6">
      <Card className="p-5">
        <SectionTitle
          title="Retenção do público"
          subtitle="% das visualizações que ainda assistiam em cada momento da aula"
        />
        <dl className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Figure
            icon={Clock}
            label="Duração média"
            value={clock(t.avg_view_duration_s)}
            hint={`de ${clock(data.duration_s)}`}
          />
          <Figure
            icon={TrendUp}
            label="Média assistida"
            value={`${t.avg_percent_viewed.toLocaleString('pt-BR')}%`}
          />
          {data.intro_retention !== null && (
            <Figure
              icon={Eye}
              label={`Depois de ${clock(Math.min(30, data.duration_s))}`}
              value={`${data.intro_retention.toLocaleString('pt-BR')}%`}
              hint="ainda assistindo"
            />
          )}
          <Figure
            icon={CheckCircle}
            label="Até o fim"
            value={`${data.retention.at(-1)!.percent.toLocaleString('pt-BR')}%`}
          />
        </dl>
        <RetentionChart data={data} drops />
        <p className="mt-2 text-xs text-fg-subtle">
          Faixas sombreadas: trechos de maior abandono. Medido em blocos de {data.bucket_s} s de reprodução
          real — arrastar a barra não conta.
        </p>
      </Card>

      <Card className="p-5">
        <SectionTitle
          title="Principais momentos"
          subtitle="Onde mais gente parou de assistir — bons candidatos a revisão"
        />
        {data.drops.length === 0 ? (
          <p className="text-sm text-fg-subtle">
            Nenhuma queda relevante: o público segue a aula de forma estável.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {data.drops.map((d) => (
              <li key={d.from_s} className="flex items-center gap-4 py-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-surface-2">
                  <Warning aria-hidden className="size-4 text-fg-muted-muted" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium tabular-nums">
                    {clock(d.from_s)} – {clock(d.to_s)}
                  </p>
                  <p className="text-xs text-fg-subtle">
                    A retenção caiu {d.drop.toLocaleString('pt-BR')} pontos percentuais neste trecho.
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ───────────── público ─────────────

const DEVICE_ICONS: Record<string, Icon> = {
  desktop: Desktop,
  mobile: DeviceMobile,
  tablet: DeviceTablet,
  tv: Television,
};

function Audience({ data }: { data: LessonInsights }) {
  if (data.totals.views === 0) {
    return <EmptyState title="Nenhuma visualização neste período" />;
  }
  const peak = peakHour(data.hours);
  const share = (list: LessonInsights['devices'], label?: (k: string) => ReactNode) =>
    list.map((s) => ({
      key: s.key,
      label: label ? label(s.key) : s.key,
      value: s.views,
      percent: s.percent,
    }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[2fr_1fr]">
        <Card className="p-5">
          <SectionTitle
            title="Quando assistem"
            subtitle={`Visualizações por hora do dia, horário de Brasília${peak ? ` · pico ${peak}` : ''}`}
          />
          <ColumnChart
            ariaLabel="Visualizações por hora do dia"
            items={data.hours.map((v, h) => ({
              label: `${h}h`,
              value: v,
              tooltip: `${h}h–${(h + 1) % 24}h`,
            }))}
            labelEvery={3}
          />
        </Card>
        <Card className="p-5">
          <SectionTitle title="Dias da semana" />
          <ColumnChart
            ariaLabel="Visualizações por dia da semana"
            items={data.weekdays.map((v, i) => ({ label: WEEKDAYS[i]!, value: v }))}
          />
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-5">
          <SectionTitle
            title="Profissão"
            subtitle="Pessoas e tempo assistido por grupo — um grupo pode ser minoria e consumir mais"
          />
          <SegmentList items={data.professions} />
        </Card>
        <Card className="p-5">
          <SectionTitle title="Origem" subtitle="Como a pessoa chegou à plataforma" />
          <SegmentList items={data.sources} />
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="p-5">
          <SectionTitle title="Dispositivos" />
          <BarList
            items={share(data.devices, (k) => {
              const I = DEVICE_ICONS[k] ?? Desktop;
              return (
                <span className="inline-flex items-center gap-2">
                  <I aria-hidden className="size-4" />
                  {DEVICE_LABELS[k] ?? k}
                </span>
              );
            })}
          />
        </Card>
        <Card className="p-5">
          <SectionTitle title="Qualidade de vídeo" subtitle="Última resolução de cada visualização" />
          <BarList items={share(data.qualities)} />
        </Card>
        <Card className="p-5">
          <SectionTitle title="Navegadores" />
          <BarList items={share(data.browsers)} />
        </Card>
        <Card className="p-5">
          <SectionTitle title="Sistemas operacionais" />
          <BarList items={share(data.os)} />
        </Card>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Users aria-hidden className="size-3.5" />
        Percentuais sobre {data.totals.views.toLocaleString('pt-BR')} visualizações no período.
      </p>
    </div>
  );
}

// ───────────── peças ─────────────

/**
 * Recorte da audiência por característica de quem assistiu.
 *
 * Mostra pessoas, tempo total e média por pessoa lado a lado: é a média que
 * revela intensidade de consumo, e a contagem sozinha a esconde.
 */
function SegmentList({ items }: { items: LessonInsights['professions'] }) {
  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-fg-subtle">Sem dados no período</p>;
  }
  const totalPeople = items.reduce((sum, i) => sum + i.people, 0);

  return (
    <BarList
      items={items.map((s) => ({
        key: s.key,
        label: (
          <span>
            {s.label}
            <span className="ml-2 text-xs text-fg-subtle">{watchTime(s.avg_per_person_s)} por pessoa</span>
          </span>
        ),
        value: s.people,
        percent: totalPeople ? Math.round((s.people / totalPeople) * 1000) / 10 : 0,
      }))}
      formatValue={(v) => `${v.toLocaleString('pt-BR')} ${v === 1 ? 'pessoa' : 'pessoas'}`}
    />
  );
}

function SectionTitle({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-fg-subtle">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Figure({
  icon: Icon,
  label,
  value,
  hint,
  warn,
}: {
  icon: Icon;
  label: string;
  value: string;
  hint?: string;
  warn?: boolean;
}) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon aria-hidden className="size-3.5" />
        {label}
      </dt>
      <dd className={cn('mt-1 text-xl font-semibold', warn && 'text-fg-muted-muted')}>{value}</dd>
      {hint && <dd className="text-xs text-fg-subtle">{hint}</dd>}
    </div>
  );
}
